import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogActions, FormHelperText, Grid, Typography,
} from '@material-ui/core';
import { Button, PhoneInput, TextField } from '@michelin/acid-components';
import { useTranslation } from '@michelin/central-provider';
import { ClosableDialogTitle } from 'components/ClosableDialog/ClosableDialogTitle';
import { ClosableDialogContent } from 'components/ClosableDialog/ClosableDialogContent';

interface AddRotationContactDialogProps {
  open: boolean;
  country: 'US' | 'CA';
  onClose: () => void;
  onSave: (data: { firstName: string; lastName: string; jobTitle: string; phoneNumber: string }) => void;
  isSaving: boolean;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
}

function AddRotationContactDialog({
  open, country, onClose, onSave, isSaving,
}: AddRotationContactDialogProps) {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneIsValid, setPhoneIsValid] = useState(true);
  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (open) {
      setFirstName('');
      setLastName('');
      setJobTitle('');
      setPhoneNumber('');
      setPhoneIsValid(true);
      setErrors({});
    }
  }, [open]);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!firstName.trim()) newErrors.firstName = t('First Name is required');
    if (!lastName.trim()) newErrors.lastName = t('Last Name is required');
    if (!phoneNumber.trim()) {
      newErrors.phoneNumber = t('Phone Number is required');
    } else if (!phoneIsValid) {
      newErrors.phoneNumber = t('Invalid phone number format');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validate()) {
      onSave({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        jobTitle: jobTitle.trim(),
        phoneNumber: phoneNumber.trim(),
      });
    }
  };

  return (
    <Dialog
      onClose={onClose}
      aria-labelledby="add-rotation-contact-dialog"
      open={open}
      fullWidth
      maxWidth="sm"
      disableBackdropClick
    >
      <ClosableDialogTitle onClose={onClose}>
        {t('Add Contact')}
      </ClosableDialogTitle>

      <ClosableDialogContent dividers>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
          {t('Staff members must be added as a contact to assign them to a rotation')}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              id="rotation-contact-first-name"
              label={t('First Name')}
              value={firstName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFirstName(e.target.value);
                if (errors.firstName) setErrors((prev) => ({ ...prev, firstName: undefined }));
              }}
              error={!!errors.firstName}
              helperText={errors.firstName}
              required
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              id="rotation-contact-last-name"
              label={t('Last Name')}
              value={lastName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setLastName(e.target.value);
                if (errors.lastName) setErrors((prev) => ({ ...prev, lastName: undefined }));
              }}
              error={!!errors.lastName}
              helperText={errors.lastName}
              required
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="rotation-contact-job-title"
              label={t('Job Title')}
              value={jobTitle}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setJobTitle(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <PhoneInput
              id="rotation-contact-phone"
              label={t('Phone Number')}
              value={phoneNumber}
              defaultCountry={country}
              onChange={(target: any) => {
                setPhoneNumber(target.value || '');
                setPhoneIsValid(target.isValid);
                if (errors.phoneNumber) setErrors((prev) => ({ ...prev, phoneNumber: undefined }));
              }}
              required={!!errors.phoneNumber}
            />
            {errors.phoneNumber && (
              <FormHelperText error>
                {errors.phoneNumber}
              </FormHelperText>
            )}
          </Grid>
        </Grid>
      </ClosableDialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          {t('Cancel')}
        </Button>
        <Button onClick={handleSave} color="primary" disabled={isSaving}>
          {isSaving ? t('Saving...') : t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddRotationContactDialog;
